import { useAuthStore } from '@common/auth-layer/store/auth.store'

import { markMemberOnboardingTasksCompleteByTypes, type WelcomeAutoActivityTaskType } from './welcome-onboarding-complete-cache.utils'
import {
  applyRememberedWelcomeCompletions,
  rememberCompletedStepsMatchingTypes,
  rememberWelcomeCompletedStepIds,
} from './welcome-onboarding-completion-memory.utils'
import { welcomeOnboardingQueryKeys } from './welcome-onboarding-query-keys.utils'
import { isWelcomeMemberOnboardingActive } from './welcome-onboarding-visit.utils'
import type { QueryClient } from '@tanstack/vue-query'
import type { MemberOnboardingResponse } from '../types/types'

function welcomeMemoryUserId(explicit?: string): string {
  if (explicit?.trim()) return explicit.trim()
  try {
    return String(useAuthStore().userId || '').trim()
  } catch {
    return ''
  }
}

/**
 * Keep only AUTO activity types that still need a board refresh.
 * If the member board is not loaded yet, keep the requested types so the first
 * activity can still sync. Once the board is cached, skip types that are absent
 * or already completed (LIKE_POST, FIRST_POST, RSVP_EVENT, COMPLETE_PROFILE, …).
 */
export function filterIncompleteAutoActivityTypes(
  queryClient: QueryClient,
  locationId: string | undefined,
  groupId: string | undefined,
  types: readonly WelcomeAutoActivityTaskType[]
): WelcomeAutoActivityTaskType[] {
  if (!locationId || !groupId || !types.length) return []
  const data = queryClient.getQueryData<MemberOnboardingResponse>(welcomeOnboardingQueryKeys.member(locationId, groupId))
  // Board not loaded yet — allow first activity sync. Once cached, require an active window.
  if (!data) return [...types]
  if (!isWelcomeMemberOnboardingActive(data)) return []
  return types.filter(type => data.tasks.some(task => task.trackingMode === 'AUTO' && task.type === type && !task.completed))
}

/** True when the member board cache still has an incomplete AUTO task of one of the given types. */
export function memberBoardHasIncompleteAutoTypes(
  queryClient: QueryClient,
  locationId: string | undefined,
  groupId: string | undefined,
  types: readonly WelcomeAutoActivityTaskType[]
): boolean {
  return filterIncompleteAutoActivityTypes(queryClient, locationId, groupId, types).length > 0
}

function applyOptimisticTick(
  queryClient: QueryClient,
  memberKey: readonly unknown[],
  locationId: string,
  groupId: string,
  types: readonly WelcomeAutoActivityTaskType[],
  userId?: string
): void {
  const memoryUserId = welcomeMemoryUserId(userId)
  queryClient.setQueryData<MemberOnboardingResponse>(memberKey, current => {
    if (!isWelcomeMemberOnboardingActive(current)) {
      return applyRememberedWelcomeCompletions(current, locationId, groupId, memoryUserId)
    }

    // Remember step ids from the pre-tick board so focus/refetch cannot wipe the tick
    // even if the optimistic mark finds nothing (stale snapshot / already-partial complete).
    if (current?.tasks?.length) {
      const typeSet = new Set<string>(types)
      const ids = current.tasks.filter(task => task.trackingMode === 'AUTO' && typeSet.has(task.type)).map(task => task.id)
      rememberWelcomeCompletedStepIds(locationId, groupId, ids, memoryUserId)
    }

    const next = markMemberOnboardingTasksCompleteByTypes(current, types)
    rememberCompletedStepsMatchingTypes(next, locationId, groupId, types, memoryUserId)
    return applyRememberedWelcomeCompletions(next, locationId, groupId, memoryUserId)
  })
}

/**
 * Instant learner checklist update after AUTO activities (like / comment / post / RSVP / profile).
 * Optimistic tick + session memory, invalidate, then re-apply after refetch —
 * BE often completes asynchronously and focus/tab refetches would otherwise wipe FE ticks.
 */
export function refreshMemberOnboardingAfterActivity(
  queryClient: QueryClient,
  locationId: string | undefined,
  groupId: string | undefined,
  types: readonly WelcomeAutoActivityTaskType[],
  userId?: string
): void {
  if (!locationId || !groupId || !types.length) return

  const typesNeedingRefresh = filterIncompleteAutoActivityTypes(queryClient, locationId, groupId, types)
  if (!typesNeedingRefresh.length) return

  const memberKey = welcomeOnboardingQueryKeys.member(locationId, groupId)
  applyOptimisticTick(queryClient, memberKey, locationId, groupId, typesNeedingRefresh, userId)

  void (async () => {
    await queryClient.invalidateQueries({ queryKey: memberKey })
    applyOptimisticTick(queryClient, memberKey, locationId, groupId, typesNeedingRefresh, userId)
  })()
}

/** Re-merge session completions after a plain board refetch (visibility / focus). */
export function reapplyRememberedMemberOnboarding(
  queryClient: QueryClient,
  locationId: string | undefined,
  groupId: string | undefined,
  userId?: string
): void {
  if (!locationId || !groupId) return
  const memberKey = welcomeOnboardingQueryKeys.member(locationId, groupId)
  const memoryUserId = welcomeMemoryUserId(userId)
  queryClient.setQueryData<MemberOnboardingResponse>(memberKey, current =>
    applyRememberedWelcomeCompletions(current, locationId, groupId, memoryUserId)
  )
}

export function rememberWelcomeStepComplete(
  locationId: string | undefined,
  groupId: string | undefined,
  stepId: string,
  userId?: string
): void {
  rememberWelcomeCompletedStepIds(locationId, groupId, [stepId], welcomeMemoryUserId(userId))
}
